import React from "react";
import {
  isHiringStrategyAOPInterview,
  MatcherInvitationContext,
} from "../../shared/types";

type InviteCopy = {
  title: string;
  body: React.ReactNode;
};

const DEFAULT_ACCEPTED_COPY: InviteCopy = {
  title: "Response Received",
  body: (
    <>
      Thank you for accepting the invitation. You’ll be hearing from our team
      shortly regarding next steps. Please also keep an eye out for a scheduling
      text from Sam, our Virtual Hiring Assistant. In the meantime, feel free to
      explore other job opportunities here:
    </>
  ),
};

const DECLINED_COPY: InviteCopy = {
  title: "Response Received",
  body: (
    <>
      Thank you for your response. We appreciate your time and interest. You can
      explore other job openings using the link below.
    </>
  ),
};

const LINK_EXPIRED_COPY: InviteCopy = {
  title: "Link Expired or Invalid",
  body: (
    <>
      Thank you for your response! This link is either no longer active or may
      be invalid. We appreciate your time and interest. Feel free to explore
      other job opportunities using the link below:
    </>
  ),
};

const SUBMIT_TO_REQ_AOP_ACCEPTED: InviteCopy = {
  title: "Response Received",
  body: (
    <>
      Thank you for accepting the invitation! You’ve been added to this
      requisition and placed in the interview scheduling queue. You’ll receive a
      text message or email shortly with instructions to schedule your
      interview.
      <br />
      <br />
      In the meantime, feel free to explore other job opportunities here:
    </>
  ),
};

const INTERVIEW_SCHEDULED_ACCEPTED: InviteCopy = {
  title: "Response Received",
  body: (
    <>
      Thank you for accepting the invitation! Your interview has already been
      scheduled. You’ll receive additional details shortly with more information
      about your upcoming interview. Please review that message carefully and be
      sure to attend at the scheduled time.
      <br />
      <br />
      In the meantime, feel free to explore other job opportunities here:
    </>
  ),
};

const INTERVIEW_NOW_ACCEPTED: InviteCopy = {
  title: "Response Received",
  body: (
    <>
      Thank you for accepting the invitation! You’ll receive a text message or
      email shortly with instructions to connect directly to your interview.
      <br />
      <br />
      If you’d like to explore other job opportunities, please visit:
    </>
  ),
};

const SUBMIT_TO_REQ_OPTIMIZED_ACCEPTED: InviteCopy = {
  title: "Response Received",
  body: (
    <>
      Thank you for accepting the invitation! You’ll receive a text message or
      email shortly with instructions to connect directly to your interview.
      <br />
      <br />
      If you’d like to explore other job opportunities, please visit:
    </>
  ),
};

export const getInviteStatusCopy = ({
  isLinkExpired,
  isInvitationAccepted,
  matcherInvitationContext,
}: {
  isLinkExpired: boolean;
  isInvitationAccepted?: boolean;
  matcherInvitationContext?: MatcherInvitationContext | null;
}): InviteCopy => {
  if (isLinkExpired) return LINK_EXPIRED_COPY;

  if (!isInvitationAccepted) return DECLINED_COPY;

  if (!matcherInvitationContext) return DEFAULT_ACCEPTED_COPY;

  const { hiringPhase, hiringStrategy } = matcherInvitationContext;

  if (hiringPhase === "INTERVIEW_SCHEDULED")
    return INTERVIEW_SCHEDULED_ACCEPTED;
  if (hiringPhase === "INTERVIEW_NOW") return INTERVIEW_NOW_ACCEPTED;

  if (hiringPhase === "SUBMIT_TO_REQUISITION") {
    return isHiringStrategyAOPInterview(hiringStrategy)
      ? SUBMIT_TO_REQ_AOP_ACCEPTED
      : SUBMIT_TO_REQ_OPTIMIZED_ACCEPTED;
  }

  return DEFAULT_ACCEPTED_COPY;
};
